/**
 * Cost Per Person Section (owner-only)
 *
 * Auto-populated from Xero Payroll: employee identity, annual package
 * (derived from payslips), employment basis and start date are synced
 * from Xero on load. The owner sets the billable flag per person — the
 * only locally-persisted field. The derived average billable package
 * feeds the scenario builder's cost-per-person labour model.
 */

import { useCallback } from 'react';
import { useSalaryRegister } from '@/hooks/useSalaryRegister';
import { CalcTooltip } from './CalcTooltip';
import { formatCurrency, formatDate } from '@/utils/formatters';
import type { SalaryPerson } from '@/types/salaryRegister';
import './CostPerPersonSection.css';

export function CostPerPersonSection({
  onRegisterChanged,
}: {
  /** Called after a successful billable toggle so the scenario builder
   * refetches its labour model immediately. */
  onRegisterChanged?: () => void;
} = {}) {
  const {
    allowed, data, loading, error, mutating, mutateError,
    refresh, setBillable,
  } = useSalaryRegister();

  const handleToggle = useCallback(
    async (person: SalaryPerson, billable: boolean) => {
      const ok = await setBillable(person.id, billable);
      if (ok) onRegisterChanged?.();
    },
    [setBillable, onRegisterChanged]
  );

  // Not the owner (legitimate 403): render nothing at all.
  // Gate broken (non-403 error, e.g. middleware crash / proxy down):
  // render a warning banner so the owner knows the auth gate may be
  // down — fail-closed (section content hidden) but visible (error
  // surfaced). Per SECURITY-STANDARDS §MISCONFIG-12: auth failures
  // must be distinguishable from legitimate denials in the UI.
  if (!allowed) {
    if (!error) return null;
    return (
      <section
        className="rt-section cpp-section"
        aria-label="Cost per person (owner only)"
      >
        <p className="cpp-error" role="alert">{error}</p>
      </section>
    );
  }

  const summary = data?.summary ?? null;
  const people = data?.people ?? [];
  const syncedAt = data?.syncedAt;

  return (
    <section className="rt-section cpp-section" aria-label="Cost per person (owner only)">
      <div className="cpp-title-row">
        <h3 className="rt-section__title">
          Cost per person
          <span className="cpp-owner-badge" title="Only you can see this section">
            Owner only
          </span>
          <span className="cpp-sync-badge" title="People and packages are fetched from Xero Payroll">
            Synced from Xero
          </span>
        </h3>
        <div className="cpp-title-actions">
          <button
            onClick={() => { void refresh(); }}
            className="cpp-refresh"
            type="button"
            disabled={loading}
            aria-label="Re-sync from Xero Payroll"
          >
            &#8635;
          </button>
        </div>
      </div>
      <p className="rt-section__note">
        Employees and annual packages are synced from Xero Payroll (derived
        from payslips). Tag each person as billable or not — the average
        billable package sets the per-person cost the scenario builder adds
        or removes when billable headcount changes.
      </p>

      {error && (
        <p className="cpp-error" role="alert">{error}</p>
      )}

      {summary && (
        <div className="cpp-summary">
          <div className="cpp-summary__item">
            <span className="cpp-summary__label">
              Cost per billable person
              <CalcTooltip title="Cost per billable person">
                <p>
                  Average monthly package (gross) across the{' '}
                  {summary.billableCount} billable{' '}
                  {summary.billableCount === 1 ? 'person' : 'people'} on this
                  register. Moving the billable-people lever in the scenario
                  builder adds or removes this amount per person per month, so
                  salary costs scale in proportion to headcount.
                </p>
              </CalcTooltip>
            </span>
            <span className="cpp-summary__value">
              {formatCurrency(summary.costPerBillablePersonMonthly)}/mo
            </span>
          </div>
          <div className="cpp-summary__item">
            <span className="cpp-summary__label">Billable people</span>
            <span className="cpp-summary__value">{summary.billableCount}</span>
          </div>
          <div className="cpp-summary__item">
            <span className="cpp-summary__label">Billable salaries</span>
            <span className="cpp-summary__value">
              {formatCurrency(summary.billableMonthly)}/mo
            </span>
          </div>
          <div className="cpp-summary__item">
            <span className="cpp-summary__label">Overhead roles</span>
            <span className="cpp-summary__value">
              {formatCurrency(summary.overheadMonthly)}/mo
            </span>
          </div>
          <div className="cpp-summary__item">
            <span className="cpp-summary__label">Total salary bill</span>
            <span className="cpp-summary__value">
              {formatCurrency(summary.totalMonthly)}/mo
            </span>
          </div>
        </div>
      )}

      {syncedAt && (
        <p className="cpp-synced-at">
          Last synced {formatDate(syncedAt, 'dd MMM yyyy HH:mm')}
        </p>
      )}

      {mutateError && (
        <p className="cpp-error" role="alert">{mutateError}</p>
      )}

      {loading && people.length === 0 && (
        <p className="cpp-muted">Syncing from Xero Payroll...</p>
      )}

      {!loading && people.length === 0 && (
        <p className="cpp-muted">
          No employees found in Xero Payroll. Ensure the Xero connection has
          Payroll API access and that there are active employees.
        </p>
      )}

      {people.length > 0 && (
        <div className="rt-table-wrap">
          <table className="rt-table">
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col">Role</th>
                <th scope="col">Annual package</th>
                <th scope="col">Monthly</th>
                <th scope="col">Start date</th>
                <th scope="col">Last paid</th>
                <th scope="col">
                  Billable
                  <CalcTooltip title="Billable flag">
                    <p>
                      Toggle on for people who do billable delivery work.
                      Only billable people are counted in the cost-per-person
                      average that drives the scenario builder. This is the
                      only field you control — everything else comes from
                      Xero Payroll.
                    </p>
                  </CalcTooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{p.role}</td>
                  <td>
                    {p.annualPackage > 0
                      ? formatCurrency(p.annualPackage)
                      : <span className="cpp-muted">No recent payslip</span>}
                  </td>
                  <td>
                    {p.annualPackage > 0
                      ? formatCurrency(p.annualPackage / 12)
                      : '—'}
                  </td>
                  <td>
                    {p.startDate
                      ? formatDate(p.startDate, 'dd MMM yyyy')
                      : '—'}
                  </td>
                  <td>
                    {p.mostRecentPayDate
                      ? formatDate(p.mostRecentPayDate, 'dd MMM yyyy')
                      : '—'}
                  </td>
                  <td>
                    <label className="cpp-billable-toggle">
                      <input
                        type="checkbox"
                        checked={p.billable}
                        onChange={(e) => { void handleToggle(p, e.target.checked); }}
                        disabled={mutating}
                        aria-label={`${p.billable ? 'Remove' : 'Set'} ${p.name} as billable`}
                      />
                      <span>{p.billable ? 'Yes' : 'No'}</span>
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
